import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { addingSideEffects, deletingSideEffects, errorSideEffects, successSideEffects } from "..";
import { Toastify } from "../../../components/Toaster";
import { getOutcomeTrackingState, getRequestTrackingState, MutationTrackingState } from "../../../services/analytics/tracking";

export interface CustomFieldMutationPayload {
  customfield: string;
}

// The saga resolves toast copy from the i18n dictionary and hands the finished string over, so these
// reducers toast where the sibling slices toast without carrying English of their own.
interface MutationOutcomePayload {
  message: string;
}

export interface CustomFieldMutationState extends MutationTrackingState {
  isAdding: boolean;
  isDeleting: boolean;
  isSuccess: boolean;
  isError: boolean;
  errorMessage: string | null;
  // Which operation the current isSuccess/isError describes. The create dialog and the delete
  // confirmation both watch this one slice, and each may only close on its own success — without
  // this they would both close whichever finished first.
  lastAction: "add" | "delete" | null;
}

const initialState: CustomFieldMutationState = {
  isAdding: false,
  isDeleting: false,
  isSuccess: false,
  isError: false,
  errorMessage: null,
  lastAction: null
};

const customFieldMutationSlice = createSlice({
  name: "customFieldMutations",
  initialState,
  reducers: {
    addCustomFieldRequest(state, action: PayloadAction<CustomFieldMutationPayload>) {
      Object.assign(state, addingSideEffects, getRequestTrackingState(action));
      state.lastAction = "add";
    },
    addCustomFieldSuccess(state, action: PayloadAction<MutationOutcomePayload>) {
      Object.assign(state, successSideEffects, getOutcomeTrackingState(state, "success"));
      state.lastAction = "add";
      Toastify("success", action.payload.message);
    },
    addCustomFieldError(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state, "error"));
      state.lastAction = "add";
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },
    deleteCustomFieldRequest(state, action: PayloadAction<CustomFieldMutationPayload>) {
      Object.assign(state, deletingSideEffects, getRequestTrackingState(action));
      state.lastAction = "delete";
    },
    deleteCustomFieldSuccess(state, action: PayloadAction<MutationOutcomePayload>) {
      Object.assign(state, successSideEffects, getOutcomeTrackingState(state, "success"));
      state.lastAction = "delete";
      Toastify("success", action.payload.message);
    },
    deleteCustomFieldError(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state, "error"));
      state.lastAction = "delete";
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    }
  }
});

export const { addCustomFieldRequest, addCustomFieldSuccess, addCustomFieldError, deleteCustomFieldRequest, deleteCustomFieldSuccess, deleteCustomFieldError } = customFieldMutationSlice.actions;

export default customFieldMutationSlice.reducer;
